<template>
  <div class="dialog-size-md user-name-and-image-dialog">
    <div class="modal-body">
      <div class="profile-image-container">
        <div class="profile-image">
          <input
            id="hiddenFileInput"
            ref="hiddenFileInput"
            class="hidden-file-input"
            type="file"
            :disabled="imageUploadInProgress"
            @change="onProfileImageChange"
          />
          <label for="hiddenFileInput">
            <img
              class="account-image"
              :src="profileImage"
              alt="profile image"
            />
            <div class="upload-indicator-wrapper">
              <div class="upload-indicator">
                <Icon icon="image-upload" />
              </div>
            </div>
          </label>
          <div v-if="imageUploadInProgress" class="loading-overlay">
            <LoadingHeart />
          </div>
        </div>
        <div>
          <p class="text-sm text-grey profile-image-hint">
            {{ $t('ACCOUNT.ACCOUNT.PROFILE_IMAGE_HINT') }}
          </p>
          <label
            class="link-main image-upload"
            for="hiddenFileInput"
            :disabled="imageUploadInProgress"
          >
            {{ $t('ACCOUNT.ACCOUNT.PROFILE_IMAGE_UPLOAD') }}
          </label>
        </div>
        <div v-if="displayUploadError">
          <Warning
            variant="error"
            subheading="Your picture does not meet the requirements"
          />
        </div>
      </div>
      <LabelInput :class="{ 'has-error': errors.username }">
        <label for="accountUserName">{{
          $t('ACCOUNT.ACCOUNT.USERNAME')
        }}</label>
        <DebounceInput
          id="accountUserName"
          type="text"
          :model-value="username"
          max-length="50"
          @update:model-value="updateUsername"
        />
        <AccountError
          v-if="errors.username"
          :reason="errors.username"
        ></AccountError>
        <p class="text-sm text-grey username-disclaimer">
          {{ $t('ACCOUNT.ACCOUNT.USERNAME_HINT') }}
          <span v-if="hasMarketplace">
            {{ $t('ACCOUNT.ACCOUNT.SHOWROOM_HINT') }}
          </span>
        </p>
      </LabelInput>
      <div class="actions">
        <ProgressButton
          class="btn btn-primary btn-lg"
          :label="$t('GENERAL.SAVE')"
          :on-click="onSave"
          :disabled="!usernameChanged"
        >
        </ProgressButton>
        <button :class="'btn btn-tonal-filled'" @click="cancel">
          {{ $t('GENERAL.CANCEL') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { mapState as mapPiniaState, mapActions } from 'pinia';
import LabelInput from 'commons/labelInput/LabelInput.vue';
import userService from 'commons/api/userService/userService';
import LoadingHeart from 'commons/LoadingHeart/LoadingHeart.vue';
import AccountError from './errorMessage/AccountError.vue';
import ProgressButton from 'commons/btnProgress/ProgressButton.vue';
import DebounceInput from 'src/app/components/input/DebounceInput.vue';
import { useIdentityStore } from 'commons/stores/identity';
import Warning from 'src/app/components/warnings/Warning.vue';

export default {
  name: 'UserNameAndImageDialog',
  components: {
    LabelInput,
    LoadingHeart,
    AccountError,
    ProgressButton,
    DebounceInput,
    Warning,
  },
  props: ['modalInstance'],
  data() {
    return {
      uploadProfileImagePromise: null,
      imageUploadInProgress: false,
      displayUploadError: false,
      errors: {
        username: null,
      },
    };
  },
  computed: {
    ...mapPiniaState(useIdentityStore, {
      username: 'customUsername',
      usernameChanged: 'usernameChanged',
    }),
    ...mapState({
      userData: (s) => s.user.data,
    }),
    ...mapGetters({
      hasMarketplace: 'user/hasMarketplace',
      profileImage: 'user/profileImage',
    }),
  },
  unmounted() {
    this.resetUsername();
  },
  methods: {
    ...mapMutations({
      reloadProfileImage: 'user/reloadProfileImage',
    }),
    ...mapActions(useIdentityStore, [
      'setUsername',
      'resetUsername',
      'saveUsername',
    ]),
    cancel() {
      this.resetUsername();
      this.modalInstance.dismiss();
    },
    updateUsername(username) {
      this.resetErrors();
      this.setUsername(username);
    },
    async onSave() {
      try {
        await this.saveUsername(this.username);
        this.modalInstance.close();
      } catch (error) {
        this.setErrors(error);
        throw error;
      }
    },
    resetErrors() {
      this.errors.username = null;
    },
    setErrors(error) {
      switch (error?.data?.error) {
        case '000000':
          this.errors.username = 'INVALID_FORMAT';
          break;
        case '000004':
          this.errors.username = 'USERNAME_EXISTS';
          break;
      }
    },
    async onProfileImageChange(e) {
      this.displayUploadError = false;
      const file = e.srcElement.files[0];
      this.$refs.hiddenFileInput.value = '';
      this.imageUploadInProgress = true;

      try {
        this.uploadProfileImagePromise =
          await userService.updateProfileImage(file);
        this.reloadProfileImage();
      } catch (_error) {
        this.displayUploadError = true;
      }
      this.imageUploadInProgress = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/buttons';
.hidden-file-input {
  // hacky way of achieving display:none without side effects, see https://tympanus.net/codrops/2015/09/15/styling-customizing-file-inputs-smart-way/
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}

.account-image {
  display: block;
  height: 120px;
  width: 120px;
  cursor: pointer;
  border-radius: 50%;
}
.profile-image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;

  & > div {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .profile-image {
    position: relative;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 24px;
    background-color: rgba(255, 255, 255, 0.9);
  }

  .profile-image-hint {
    margin: 0 0 8px 0;
    transition: all 0.5s ease;
  }

  .image-upload {
    margin: 0;
    position: relative;

    .icon {
      font-size: inherit;
    }
  }
}

.username-disclaimer {
  margin: 8px 0 0 0;
}

.actions {
  display: flex;
  gap: 16px;
  margin-top: 16px;

  .btn {
    flex: 1;
    padding: 8px 24px;
  }
}

.upload-indicator-wrapper {
  display: none;
}

.profile-image:hover .upload-indicator-wrapper {
  display: flex;
  z-index: 2;
  background-color: rgba(0, 0, 0, 0.8);
  cursor: pointer;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;

  .upload-indicator {
    color: $pa-color-main;
    padding: 8px;
    border-radius: 100%;
    background-color: $white;

    .icon {
      width: 28px;
      height: 28px;
    }
  }
}
</style>
